"use client";

import Link from "next/link";
import { Suspense, type ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { buildLoginHref } from "@/modules/auth/utils/loginRedirect";

type LoginLinkProps = {
  /** Already-localized login path, e.g. `/mena/en/login`. */
  loginHref: string;
  className?: string;
  onClick?: () => void;
  children: ReactNode;
};

/**
 * A "Login" link that remembers the full current location (pathname + query)
 * so the user is returned there after signing in — e.g. clicking Login from
 * `/en/courses?category_id=3&page=2` comes back to that exact URL. The return
 * path funnels through `buildLoginHref` → `sanitizeReturnPath`, so the
 * open-redirect protection applies here too.
 *
 * Centralizes the capture for every navbar entry point; other capture sites
 * (dashboard auth guard, booking prompts) build their own href from data they
 * already hold.
 */
function LoginLinkInner({ loginHref, className, onClick, children }: LoginLinkProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const returnTo = `${pathname}${search ? `?${search}` : ""}`;

  return (
    <Link
      href={buildLoginHref(loginHref, returnTo)}
      className={className}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

export default function LoginLink(props: LoginLinkProps) {
  // `useSearchParams` must sit under a Suspense boundary, otherwise a page that
  // renders this link through the shared navbar is forced into fully client-side
  // rendering and `next build`'s static prerender errors. The fallback is a
  // plain login link (no return param) for the brief prerender window.
  return (
    <Suspense
      fallback={
        <Link
          href={props.loginHref}
          className={props.className}
          onClick={props.onClick}
        >
          {props.children}
        </Link>
      }
    >
      <LoginLinkInner {...props} />
    </Suspense>
  );
}
